<template>
    <div v-if="activeSystem">
        <page-header
            :tabBar="true"
            :text="$t('navigation.systems.management')"
            :button="{ text: $t('buttons.reload'), emitEvent: 'reloadData', ability: true }"
            icon="action-sync"
            :iconClass="reloadIconClass"
            @reloadData="reloadData"
        />

        <page-subnavigation :items="allSystemLinks" class="slab-zero--bottom" />

        <div class="table-wrapper">
            <table id="system.webserverupdate.currentstatus" class="table table--striped-dynamic table--striped-dynamic--unbordered slab-mobile--large--bottom">
                <thead class="table__head table__head--dark table__head--prominent table__head--sticky">
                    <tr>
                        <th class="table__data table__data--label">
                            {{ $t('system.webserverupdate.currentstatus') }}
                        </th>
                        <th class="table__data table__data--parameter"></th>
                        <th class="table__data table__data--parameter"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr id="system.webserverupdate.device" v-if="deviceInfo && deviceInfo.device">
                        <td>{{ $t('system.webserverupdate.device') }}</td>
                        <td>{{ deviceInfo.device }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.serial_number" v-if="deviceInfo && deviceInfo.serial_number">
                        <td>{{ $t('fields.serial_number') }}</td>
                        <td>{{ deviceInfo.serial_number }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.mac_address" v-if="activeSystem && activeSystem.mac_address">
                        <td>{{ $t('fields.mac_address') }}</td>
                        <td>{{ activeSystem.mac_address }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.version" v-if="deviceInfo && deviceInfo.version">
                        <td>{{ $t('system.webserverupdate.version') }}</td>
                        <td>{{ deviceInfo.version }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.stack" v-if="deviceInfo && deviceInfo.vsnStack">
                        <td>{{ $t('system.webserverupdate.stack') }}</td>
                        <td>{{ deviceInfo.vsnStack }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.gui" v-if="deviceInfo && deviceInfo.vsnGui">
                        <td>{{ $t('system.webserverupdate.gui') }}</td>
                        <td>{{ deviceInfo.vsnGui }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.domain" v-if="activeSystem && activeSystem.url">
                        <td>{{ $t('system.webserverupdate.domain') }}</td>
                        <td>{{ activeSystem.url | formatDomain }}</td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="table-wrapper">
            <table id="system.webserverupdate.updateserver" class="table table--striped-dynamic table--striped-dynamic--unbordered slab-mobile--large--bottom">
                <thead class="table__head table__head--dark table__head--prominent table__head--sticky">
                    <tr>
                        <th class="table__data table__data--label">
                            {{ $t('system.webserverupdate.updateserver') }}
                        </th>
                        <th class="table__data table__data--parameter"></th>
                        <th class="table__data table__data--parameter"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="updateStatus" id="system.webserverupdate.version">
                        <td>{{ $t('system.webserverupdate.version') }}</td>
                        <td>{{ nextUpdateVersion.GUI || '-' }}</td>
                        <td>
                            <button
                                class="button button--primary"
                                :class="{
                                    disabled: !nextUpdateVersion.enabled || !deviceInfoLoaded,
                                    'button--running': updateStatus && updateStatus.Running,
                                }"
                                @click="startUpdate"
                            >
                                <span v-if="updateStatus && updateStatus.Running">{{ $t('system.webserverupdate.running') }}</span>
                                <span v-else>{{ $t('system.webserverupdate.update') }}</span>
                            </button>
                            &nbsp;&nbsp;
                            <transition name="fade"><i class="icon icon--common-spinner spinner" v-if="updateStatus && updateStatus.Running && !updateStatus.Finished"></i></transition>
                        </td>
                    </tr>
                    <tr v-else-if="!updateError && deviceInfoLoaded" id="system.webserverupdate.link">
                        <td>{{ $t('system.webserverupdate.link') }}</td>
                        <td width="30%">
                            <a class="underlined" :href="`${this.activeSystem.url}/update`" target="_blank">
                                {{ this.activeSystem.url }}/update
                                <i class="icon icon--external-link"></i>
                            </a>
                        </td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="table-wrapper">
            <table id="system.webserverupdate.status" class="table table--striped-dynamic table--striped-dynamic--unbordered slab-mobile--large--bottom">
                <thead class="table__head table__head--dark table__head--prominent table__head--sticky">
                    <tr>
                        <th class="table__data table__data--label">
                            {{ $t('system.webserverupdate.status') }}
                        </th>
                        <th class="table__data table__data--parameter"></th>
                        <th class="table__data table__data--parameter"></th>
                    </tr>
                </thead>
                <tbody v-if="updateStatus">
                    <tr id="system.webserverupdate.running">
                        <td>{{ $t('system.webserverupdate.running') }}</td>
                        <td>{{ updateStatus.Running ? $t('common.yes') : $t('common.no') }}</td>
                        <td></td>
                    </tr>
                    <!-- <tr id="system.webserverupdate.finished">
                        <td>{{ $t('system.webserverupdate.finished') }}</td>
                        <td>{{ updateStatus.Finished ? $t('common.yes') : $t('common.no') }}</td>
                        <td></td>
                    </tr> -->
                    <tr id="system.webserverupdate.exitcode">
                        <td>{{ $t('system.webserverupdate.exitcode') }}</td>
                        <td>{{ updateStatus.ExitCode }}</td>
                        <td></td>
                    </tr>
                    <tr>
                        <td>{{ $t('system.webserverupdate.error') }}</td>
                        <td>{{ updateStatus.Error || '-' }}</td>
                        <td></td>
                    </tr>
                    <tr id="system.webserverupdate.timestarted">
                        <td>{{ $t('system.webserverupdate.timestarted') }}</td>
                        <td>{{ updateStatus.TimeStarted || '-' }}</td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapState, mapGetters } from 'vuex';
import PageHeader from '@/components/PageHeader';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    components: {
        PageHeader,
        PageSubnavigation,
    },
    mixins: [generalLinks],
    filters: {
        formatDomain(value) {
            let urlParts = value.split('.');
            urlParts.shift();
            return urlParts.join('.');
        },
    },
    data() {
        return {
            reloading: false,
            loading: false,
            deviceInfo: false,
            updateStatus: false,
            updateStatusInterval: false,
            nextUpdateVersion: false,
            updateError: false,
            deviceInfoLoaded: false,
        };
    },
    computed: {
        ...mapState(['activeSystem']),
        ...mapGetters(['userOrganizationId', 'isParentOrganization']),
        reloadIconClass() {
            return `link--spinnable ${this.reloading ? 'spin' : ''}`;
        },
    },
    watch: {
        activeSystem() {
            this.getAllData();
        },
    },
    created() {
        this.getAllData();
    },
    beforeDestroy() {
        this.clearUpdateStatusInterval();
    },
    methods: {
        reloadData() {
            this.reloading = true;
            this.deviceInfo = false;
            this.updateStatus = false;
            this.nextUpdateVersion = false;
            this.updateError = false;
            this.deviceInfoLoaded = false;
            this.clearUpdateStatusInterval();
            this.getAllData();
            setTimeout(() => {
                this.reloading = false;
            }, 500);
        },
        getAllData() {
            this.fetchDeviceInfo();
            this.fetchUpdateStatus();
        },
        async fetchDeviceInfo() {
            this.updateError = false;
            this.deviceInfoLoaded = false;
            if (!this.activeSystem) return;
            try {
                const response = await api.systems.getDeviceInfo(this.activeSystem.id);
                if (response && response.data) {
                    this.deviceInfo = response.data;
                    this.fetchUpdateVersion(this.deviceInfo.version);
                }
                this.deviceInfoLoaded = true;
            } catch (error) {
                console.error(error);
                this.updateError = true;
                this.deviceInfoLoaded = true;
            }
        },
        async fetchUpdateStatus() {
            if (!this.activeSystem) return;
            try {
                const response = await api.systems.getUpdateStatus(this.activeSystem.id);
                if (response && response.data) {
                    this.updateStatus = response.data;
                    if (response.data.Running && !response.data.Finished && !this.updateStatusInterval) {
                        this.startUpdateStatusInterval();
                    } else if ((!response.data.Running || response.data.Finished) && this.updateStatusInterval) {
                        this.clearUpdateStatusInterval();
                        this.fetchDeviceInfo();
                    }
                }
            } catch (error) {
                console.error(error);
            }
        },
        async fetchUpdateVersion(currentVersion) {
            try {
                const serialPart = this.activeSystem.serial_number.slice(0, 6);
                const response = await api.systems.getNextUpdateVersion(serialPart);

                if (response) {
                    this.nextUpdateVersion = response.data;
                    this.nextUpdateVersion.GUI = response.data.GUI.toLowerCase()
                        .replace('v', '')
                        .replace(' ', '');
                    const nextVersion = response.data.GUI.toLowerCase()
                        .replace('v', '')
                        .replace(' ', '');

                    const nextVersionParts = nextVersion.trim().split('.');
                    const currentVersionParts = currentVersion.trim().split('.');

                    for (let i = 0; i < nextVersionParts.length; i++) {
                        if (Number(nextVersionParts[i]) > Number(currentVersionParts[i])) {
                            this.nextUpdateVersion.enabled = true;
                            break;
                        }
                    }
                }
            } catch (error) {
                console.error(error);
                if (!this.nextUpdateVersion) this.nextUpdateVersion = {};
                this.nextUpdateVersion.enabled = false;
            }
        },
        async startUpdate() {
            if (!this.activeSystem) return;
            try {
                const response = await api.systems.startWebserverUpdate(this.activeSystem.id);
                if (response) {
                    this.fetchUpdateStatus();
                }
            } catch (error) {
                console.error(error);
            }
        },
        startUpdateStatusInterval() {
            this.updateStatusInterval = setInterval(() => {
                this.fetchUpdateStatus();
            }, 5000);
        },
        clearUpdateStatusInterval() {
            if (this.updateStatusInterval) {
                clearInterval(this.updateStatusInterval);
                this.updateStatusInterval = false;
            }
        },
    },
};
</script>
